import { useEffect, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "core/store/reduxStore";
import { useTheme } from "styled-components";

import { UserClaims, AuthContext } from "core/contexts/auth/auth";
import { getCasesMetrics } from "modules/ac2/services/alertService";
import { getUsersService } from "core/services/ac2Flywheel/userService";
import { exportToCSV } from "core/hooks/tableToCsv";
import { getFilterCount, getFilters } from "core/hooks/filters";
import { Toast as showToast } from "core/components/global/toast/toast";

import { 
  assignUserToAlert, 
  fetchAllAlertsByPage, 
  setAlertStatus, 
  setAlertsByPage, 
  setExportLoading, 
  setSelectedRow, 
  setSideModal, 
  setWeeklyAlerts,
  setPreviewWidth 
} from "modules/ac2/store/alert";
import AlertSidePanel from "./alertSidePanel";

import { IHeadCell } from "core/components/global/tables/datagrid/dataGridHeader/datagridHeader";
import GridPageLayout from "core/components/layout/grid/gridLayout/gridPageLayout";
import AlertsToolbar from "./alertsToolbar";
import Datagrid from "core/components/global/tables/datagrid/dataGrid";
import GridItem from "core/components/layout/grid/gridItem/gridItem";
import { setFiltersApplied } from "core/store/shared/header";


export interface ISortData {
  field: string;
  order: string;
}

export interface ShowMoreState {
  [key: string]: boolean;
}

interface Params {
  target: string;
  aFAccessToken: string;
  page: number;
  size: number;
  orderBy: string;
  order: string;
  filters: object;
  search: string;
}

export default function CategoryAlertsTable() {
  const { t } = useTranslation();
  const { checkAFToken } = useContext(AuthContext)
  const theme = useTheme();  
  const alertState = useAppSelector((state) => state.alerts)
  const dispatch = useAppDispatch();
  const [ users, setUsers ] = useState<UserClaims[]>([]);

  // const [topGridBoxdata, setTopGridBoxdata] = useState<AlertsTopGridBox[]>([
  const [topGridBoxdata, setTopGridBoxdata] = useState<any>([
    { num: 0, type: t("TOP_GRID_BOX.TOTAL_CASES"), color: "fff" },
    { num: 0, type: t("TOP_GRID_BOX.CRITICAL"), color: "fff" },
    { num: 0, type: t("TOP_GRID_BOX.HIGH"), color: "fff" },
    { num: 0, type: t("TOP_GRID_BOX.MEDIUM"), color: "fff" },
    { num: 0, type: t("TOP_GRID_BOX.LOW"), color: "fff" },
    { num: 0, type: t("TOP_GRID_BOX.INFORMATIVE"), color: "fff" },
  ]);

  const statusMapping = {
    [t("STATUS.OPEN")]: theme.colors.open_incident,
    [t("STATUS.IN_PROGRESS")]: theme.colors.in_progress_incident,
    [t("STATUS.CLOSED")]: theme.colors.closed_incident,
    [t("STATUS.REQUEST_TUNING")]: theme.colors.requested_incident
  }

  /**
   * Fetches the full list of active and inactive users on this platform
   */
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = await checkAFToken();
        const config = { 'Authorization': token, 'Content-Type': 'application/json' };      
        const usersData = await getUsersService(config);
        setUsers(usersData);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    
    fetchUsers();
  }, []);

  const handleSetStatus = async (status: string, id: string) => {
    if (status) {
      const token = await checkAFToken();
      dispatch(setAlertStatus({'aFAccessToken': token, 'status': status, 'id': id}));
    }
  }
  useEffect(() => {
    return () => {
      // This will clear the selected row on unmount/navigation
      dispatch(setSelectedRow(""));
    };
  }, [dispatch]);

  /**
   * Function to manage the assigned to field
   * @param email 
   * @param id 
   */
  const handleAssignedTo = async (email: string, id: string) => {
    const token = await checkAFToken();
    if (email) {
      dispatch(assignUserToAlert({'aFAccessToken': token, 'email': email, 'id': id}));
    } else {
      dispatch(assignUserToAlert({'aFAccessToken': token, 'email': '', 'id': id}));
    }
  }

  async function getGridBoxData() {
    const token = await checkAFToken();
    const config = { 'Authorization': token, 'Content-Type': 'application/json' };
    const gridBoxData = await getCasesMetrics(config);
    setTopGridBoxdata(gridBoxData);
  }

  /**
   * Update the gridData
   */
  useEffect(() => {
    getGridBoxData();
  }, [alertState.pageEvents])

  /**
   * Set the states and function to handle the sorting and filtering functionalities 
   * within the common table component
   */
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filterData, setFilterData] = useState({ "totalFilters": 0, "filterTerms": {}, "searchTerm": ""});
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sortData, setSortData] = useState<ISortData>({
    field: "alertId",
    order: "desc",
  });

  async function onSortChange(field: string, order: string) {
    setSortData({ field, order });
    handleFilterData({
      filter: {}, 
      argSortOrder: order, 
      argSortField: field === "id" ? "alertId" : field
    });
  }

  useEffect(() => {
    // Check URL parameters for filters
    const urlParams = new URLSearchParams(window.location.search);
    const filterParams: Record<string, string> = {};
    
    // Extract framework filter if present
    const frameworkParam = urlParams.get('filter[framework]');
    if (frameworkParam) {
      filterParams['framework'] = frameworkParam;
    }
    
    // Extract rule filter if present
    const ruleParam = urlParams.get('filter[rule]');
    if (ruleParam) {
      filterParams['rule'] = ruleParam;
    }
    
    // Get page size if present
    const sizeParam = urlParams.get('size');
    if (sizeParam) {
      const size = parseInt(sizeParam, 10);
      if (!isNaN(size)) {
        setPageSize(size);
      }
    }
    
    // Get page number if present
    const pageParam = urlParams.get('page');
    if (pageParam) {
      const pageNum = parseInt(pageParam, 10);
      if (!isNaN(pageNum)) {
        setPage(pageNum);
      }
    }
    
    // Get sort order if present
    const order = urlParams.get('order');
    const orderBy = urlParams.get('orderBy');
    if (order && orderBy) {
      setSortData({ field: orderBy, order: order });
    }
    
    // Apply filters if any were found in URL
    if (Object.keys(filterParams).length > 0) {
      console.log('Applying URL filters:', filterParams);
      handleFilterData({filter: filterParams});
    } else {
      handleFilterData({filter: {}});
    }
  }, []);
  
  async function onPageChange(pageNum) {
    handleFilterData({filter: {}, argPageNum: pageNum});
  }
  
  async function handleRowsPerPageChange(pageSize) {
    setPageSize(pageSize)
    handleFilterData({filter: {}, argPageSize: pageSize});
  }

  async function clearSearch() {
    const token = await checkAFToken();

    setFilterData(prev => ({
      filterTerms: filterData["filterTerms"],
      totalFilters: getFilterCount("", filterData["filterTerms"]),
      searchTerm: ""
    }));

    dispatch(
      setAlertsByPage(
        {
          target: "cases",
          aFAccessToken: token, 
          page: page,
          size: pageSize,
          orderBy: sortData.field,
          order: sortData.order,
          filters: filterData["filterTerms"],
          search: ""
        }
      )
    );
  }

  async function deleteFilter(key, value) {
    const token = await checkAFToken();

    const tempTerms: { [key: string]: any } = filterData["filterTerms"]
    const filterValues = tempTerms[key];

    if (filterValues) {
      tempTerms[key] = filterValues.split(',').filter(item => item !== value).join(',');
      
      for (const key in tempTerms) {
        if (tempTerms[key] === "") {
          delete tempTerms[key];
        }
      }

      setFilterData(prev => ({
        filterTerms: tempTerms,
        totalFilters: getFilterCount(filterData["searchTerm"], filterData["filterTerms"]),
        searchTerm: filterData["searchTerm"]
      }));

      dispatch(
        setAlertsByPage(
          {
            target: "cases",
            aFAccessToken: token, 
            page: page,
            size: pageSize,
            orderBy: sortData.field,
            order: sortData.order,
            filters: tempTerms ? tempTerms : filterData["filterTerms"],
            search: filterData["searchTerm"]
          }
        )
      );
    }
  }

  async function clearFilters() {
    const token = await checkAFToken();

    setFilterData(prev => ({
      filterTerms: {},
      totalFilters: 0,
      searchTerm: ""
    }));

    dispatch(
      setAlertsByPage(
        {
          target: "home",
          aFAccessToken: token, 
          page: page,
          size: pageSize,
          orderBy: sortData.field,
          order: sortData.order,
          filters: {},
          search: ""
        }
      )
    );
  }
  
  // Clear Filter button for the Mobile Desktop
  const headerState = useAppSelector((state) => state.header);
  useEffect(() => {
    clearFilters();
    clearSearch();
    dispatch(setFiltersApplied(false));
  }, [headerState.clearFilters]);

  async function handleSearchValue({search}) {
    console.log("handleSearchValue called with search:", search);
    // Verify JWT from auth server
    const token = await checkAFToken();

    // Build Params for the HTTP request
    const params: Params = {
      target: "cases",
      aFAccessToken: token, 
      page: page,
      size: pageSize,
      orderBy: sortData.field,
      order: sortData.order,
      filters: filterData["filterTerms"],
      search: search
    }
    
    // Send the HTTP request
    dispatch(setAlertsByPage(params));
  
    setFilterData({
      totalFilters: getFilterCount(search, filterData["filterTerms"]),
      filterTerms: filterData["filterTerms"],
      searchTerm: search
    })

    // For Mobile/Tablet update the header widget state
    dispatch(setFiltersApplied(true))
  }

  async function handleFilterData({
      filter, 
      argPageSize = pageSize, 
      argPageNum = page,
      argSortField = sortData.field, 
      argSortOrder = sortData.order
    }) {
    const token = await checkAFToken();

    const params: Params = {
      target: "cases",
      aFAccessToken: token, 
      page: argPageNum,
      size: argPageSize,
      orderBy: argSortField,
      order: argSortOrder,
      filters: filterData["filterTerms"],
      search: filterData["searchTerm"]
    }
        
    // A new filter has been provided, parse an add replace the existing
    params.filters = getFilters(filter, params);

    // Request the filtered data from the backend
    dispatch(setAlertsByPage(params));
  
    setFilterData({
      totalFilters: getFilterCount(filterData["searchTerm"], params.filters),
      filterTerms: params.filters,
      searchTerm: filterData["searchTerm"]
    })

    // For Mobile/Tablet update the header widget state
    if(Object.keys(filter).length !== 0 || filterData["searchTerm"] !== "") {
      dispatch(setFiltersApplied(true))
    }
  }

  /**
   * Export the filtered data to Excel
   */
  async function handleExportButton() {
    dispatch(setExportLoading(true));
    const token = await checkAFToken();

    const params: Params = {
      target: "cases",
      aFAccessToken: token, 
      page: page,
      size: pageSize,
      orderBy: sortData.field, 
      order: sortData.order,
      filters: filterData["filterTerms"],
      search: filterData["searchTerm"]
    }

    const pageEvents = await dispatch(fetchAllAlertsByPage(params));
    const payload: any = pageEvents["payload"]
    
    await exportToCSV(
      payload["exportEvents"]["records"]
    );

    dispatch(setExportLoading(false))
  }

  /**
   * Handle opening the preview pane with alert details
   * @param id 
   */
  const onAlertClick = async (id) => {    
    console.log('onAlertClick called with ID:', id);
    if (id) {
      try {
        // Set the selected row ID and open the preview pane
        dispatch(setSelectedRow(id));
        
        // Make sure the panel is expanded to a reasonable width first
        const currentWidth = alertState.previewWidth;
        if (currentWidth < 10) {
          dispatch(setPreviewWidth(30)); // 30% of container width
        }
        
        // Small delay to ensure width is set before opening the panel
        setTimeout(() => {
          dispatch(setSideModal(true));
        }, 10);
        
        console.log('Redux actions dispatched');
      } catch (error) {
        console.error("Error in onAlertClick:", error);
      }
    }
  };

  /**
   * Handle actions from the action popup
   */
  const onActionClick = (rowId: string, actionType: string) => {
    console.log(`Action clicked: ${actionType} for row ${rowId}`);
    // The action handling is implemented in the ActionPopup component
  };

  const handleFilterCreated = () => {
    // Show your toast here (e.g., using notistack, MUI Snackbar, or your own)
    showToast("Custom Filter Successfully Created", "success");
    console.log("Filter created successfully");
  };

  useEffect(() => {
    // If not currently fetching and has already instanciated the first call then fetch on the interval
    const fetchData = async () => {
      const token = await checkAFToken();
      // Get new data
      await dispatch(setWeeklyAlerts(token));
    };

    fetchData();
    
    // // Set the interval, every X seconds 'fetchData()' will be called
    // const intervalId = setInterval(fetchData, 30 * 1000);
    
    // // Once the component closes, close this interval: only fetch when in focus
    // return () => {
    //     clearInterval(intervalId);
    // };
  }, [page, pageSize, sortData, filterData]);

  return(
    <>
      <div className="flex h-full overflow-hidden" style={{ width: '100%', height: '100%' }}>
        <div className={`${alertState.previewWidth === 100 ? "" : "ps-3 pb-3 pe-3"} flex-grow flex-shrink-1 overflow-scroll`} 
        style={{ 
          flexGrow: 0, 
          flexShrink: 1,
          width: `${alertState.previewWidth ? (100 - alertState.previewWidth) : 100}%`,
          transition: 'width 0.2s ease-in-out'
        }}>
          <GridPageLayout {...topGridBoxdata} />

          <AlertsToolbar 
            rowData={alertState.pageEvents} 
            headCells={alertState.alertHeaders as IHeadCell[]}
            setFilterData={handleFilterData}
            setSearchValue={handleSearchValue}
            isFilterModalOpen={isFilterModalOpen}
            setPage={setPage}
            setIsFilterModalOpen={setIsFilterModalOpen} 
            handleExportButton={handleExportButton}
            filterData={filterData}
            deleteFilter={deleteFilter}
            clearSearch={clearSearch}
            onFilterCreated={handleFilterCreated}
          />

          <div className="mt-3">
            <GridItem title={""}>
              <Datagrid
                rowData={alertState.pageEvents}
                headCells={alertState.alertHeaders as IHeadCell[]}
                visibleKeys={alertState.alertHeaders
                  .filter((header) => header.showColumnUnExpanded)
                  .map((header) => header.id)}
                maxWidths={{
                  entity: 200,
                  classification: 200,
                }}
                showSelectionCheckbox={false}
                showPagination={true}
                pageSize={pageSize}
                totalData={alertState.totalRowCount}
                showCustomSort={true}
                isFilterModalOpen={isFilterModalOpen}
                setIsFilterModalOpen={setIsFilterModalOpen}
                defaultSorterColumn={sortData?.field}
                onRowClick={onAlertClick}
                highlightedRowId={alertState.selectedRow}
                handleAssignedTo={handleAssignedTo}
                users={users}
                setPage={setPage}
                page={page}
                isPageLoading={alertState.areUserEventsLoading}
                statusMapping={statusMapping}
                onRowsPerPageChange={handleRowsPerPageChange}
                onPageChange={onPageChange}
                onSortChange={onSortChange}
                handleSetStatus={handleSetStatus}
                onActionClick={onActionClick}
              />
            </GridItem>
          </div>
        </div>
        
        {/* Resizable Side Panel for the Summary Section*/}
        <AlertSidePanel />
      </div>
    </>
  )
}